import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import LiveChat from '../components/Live/LiveChat';
import ShareModal from '../components/Modals/ShareModal';
import { mockLiveStreams } from '../data/mockData';

const { 
  FiArrowLeft, FiHeart, FiShare2, FiMaximize2, 
  FiShield, FiUsers, FiVideo, FiSettings, 
  FiExternalLink, FiInfo, FiActivity
} = FiIcons;

const { FaYoutube, FaFacebook, FaInstagram, FaGlobe } = FaIcons;

const StreamPlayer = ({ stream, platform }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, [platform]);

  const renderContent = () => {
    switch (platform) {
      case 'youtube':
        return (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${stream.embedId || 'dQw4w9WgXcQ'}?autoplay=1&mute=0`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        );
      case 'facebook':
        return (
          <iframe 
            src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(stream.streamUrl || 'https://www.facebook.com/facebook/videos/10153231339981729/')}&show_text=0&width=560`} 
            className="w-full h-full"
            style={{ border: 'none', overflow: 'hidden' }} 
            scrolling="no" 
            frameBorder="0" 
            allowFullScreen={true} 
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          />
        );
      case 'instagram':
        return (
          <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-purple-900 to-red-900 p-8 text-center text-white">
            <SafeIcon icon={FaInstagram} className="w-20 h-20 mb-6" />
            <h3 className="text-2xl font-serif mb-4">Live on Instagram</h3>
            <p className="text-white/60 mb-8 max-w-sm">Instagram Live streams are currently only viewable through the Instagram app or website directly.</p>
            <a 
              href={`https://instagram.com/${stream.username || 'eternal_memories'}/live`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-purple-900 px-8 py-4 rounded-2xl font-black uppercase tracking-widest flex items-center gap-3 transition-transform hover:scale-105"
            >
              Open Instagram Live <SafeIcon icon={FiExternalLink} />
            </a>
          </div>
        );
      default:
        return (
          <div className="relative w-full h-full flex flex-col items-center justify-center p-8 text-center">
            <img 
              src={stream.thumbnail} 
              className="absolute inset-0 w-full h-full object-cover opacity-20 blur-2xl scale-110 pointer-events-none" 
              alt="" 
            />
            <div className="relative z-10">
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-32 h-32 bg-white/5 rounded-[40px] flex items-center justify-center mx-auto mb-10 border border-white/10 backdrop-blur-xl shadow-2xl"
              >
                <div className="relative">
                  <SafeIcon icon={FiVideo} className="w-12 h-12 text-accent-gold animate-pulse" />
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full border-2 border-black animate-ping" />
                </div>
              </motion.div>
              <h2 className="text-white font-serif text-4xl md:text-5xl mb-6 leading-tight">Private Sanctuary Broadcast</h2>
              <div className="flex flex-col items-center gap-4">
                <div className="inline-flex items-center gap-3 text-white/40 text-[10px] font-black uppercase tracking-[0.2em] bg-white/5 px-8 py-4 rounded-full border border-white/10 backdrop-blur-md">
                  <SafeIcon icon={FiShield} className="text-accent-gold" /> End-to-End Encrypted Secure Line
                </div>
                <p className="text-white/30 text-xs italic">Waiting for host to transmit data...</p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="relative w-full h-full bg-black overflow-hidden">
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div 
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-accent-navy flex flex-col items-center justify-center"
          >
            <div className="w-16 h-16 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin mb-4" />
            <p className="text-accent-gold text-[10px] font-black uppercase tracking-widest">Connecting Sanctuary</p>
          </motion.div>
        ) : (
          <motion.div 
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full h-full"
          >
            {renderContent()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LiveStreamView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const stream = mockLiveStreams.find(s => s.id === id) || mockLiveStreams[0];
  
  const [activePlatform, setActivePlatform] = useState(stream.platform || 'internal');
  const [reactions, setReactions] = useState([]);
  const [viewerCount, setViewerCount] = useState(stream.viewers);
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  // Build a stable share URL for the current stream
  const shareUrl = `${window.location.origin}/#/live/${stream.id}`;

  useEffect(() => {
    const interval = setInterval(() => {
      setViewerCount(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Close source menu when clicking outside
  useEffect(() => {
    if (!showSourceMenu) return;
    const handler = (e) => {
      if (!e.target.closest('[data-source-menu]')) setShowSourceMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSourceMenu]);

  const addReaction = (type) => {
    const rid = Date.now();
    const x = Math.random() * 80 + 10;
    const duration = 3 + Math.random() * 2;
    setReactions(prev => [...prev, { id: rid, type, x, duration }]);
    setTimeout(() => {
      setReactions(prev => prev.filter(r => r.id !== rid));
    }, duration * 1000);
  };

  const platforms = [
    { id: 'internal', name: 'EternalStream', icon: FaGlobe, color: 'text-accent-gold' },
    { id: 'youtube', name: 'YouTube', icon: FaYoutube, color: 'text-red-500' },
    { id: 'facebook', name: 'Facebook', icon: FaFacebook, color: 'text-blue-600' },
    { id: 'instagram', name: 'Instagram', icon: FaInstagram, color: 'text-pink-500' }
  ];

  return (
    <>
      <div className="min-h-screen bg-accent-navy pt-20 flex flex-col lg:flex-row overflow-hidden">
        {/* Main Stream Area */}
        <div className="flex-grow flex flex-col relative h-[50vh] lg:h-auto">
          {/* Top Header Bar */}
          <div className="p-4 lg:p-6 flex items-center justify-between bg-black/60 backdrop-blur-xl z-[60] border-b border-white/5 shadow-2xl">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/live-events')} 
                className="p-2.5 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-all"
              >
                <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-white font-serif text-lg md:text-xl line-clamp-1">{stream.title}</h1>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 bg-red-600 text-white text-[8px] font-black uppercase px-2 py-0.5 rounded-sm shadow-lg shadow-red-600/20">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Live
                  </span>
                  <span className="text-white/40 text-[9px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                    <SafeIcon icon={FiUsers} className="w-3.5 h-3.5 text-accent-gold" /> {viewerCount} Community Members
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Source Menu */}
              <div className="relative" data-source-menu>
                <button 
                  onClick={() => setShowSourceMenu(!showSourceMenu)}
                  className={`p-3 rounded-xl transition-all border border-white/10 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${showSourceMenu ? 'bg-accent-gold text-white border-accent-gold' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'}`}
                >
                  <SafeIcon icon={FiSettings} />
                  <span className="hidden md:inline">Stream Source</span>
                </button>

                <AnimatePresence>
                  {showSourceMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-3 w-56 bg-accent-navy border border-white/10 rounded-2xl shadow-2xl p-2 z-[70] backdrop-blur-2xl"
                    >
                      <p className="px-3 py-2 text-[8px] font-black uppercase tracking-[0.2em] text-white/30 mb-1">Switch Sanctuary Platform</p>
                      {platforms.map(p => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setActivePlatform(p.id);
                            setShowSourceMenu(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-3 rounded-xl text-xs font-bold transition-all ${activePlatform === p.id ? 'bg-white/10 text-white' : 'text-white/50 hover:bg-white/5 hover:text-white'}`}
                        >
                          <div className="flex items-center gap-3">
                            <SafeIcon icon={p.icon} className={p.color} />
                            {p.name}
                          </div>
                          {activePlatform === p.id && <div className="w-1.5 h-1.5 rounded-full bg-accent-gold ring-4 ring-accent-gold/20" />}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* ✅ FIXED: Share button now opens ShareModal */}
              <button 
                onClick={() => setIsShareOpen(true)}
                className="p-3 bg-white/5 text-white/60 rounded-xl hover:bg-white/10 hover:text-white transition-all border border-white/10"
                title="Share this stream"
              >
                <SafeIcon icon={FiShare2} />
              </button>
            </div>
          </div>

          {/* Video Player Container */}
          <div 
            className="flex-grow relative overflow-hidden group"
            onMouseEnter={() => setIsHoveringVideo(true)}
            onMouseLeave={() => setIsHoveringVideo(false)}
          >
            <StreamPlayer stream={stream} platform={activePlatform} />

            {/* Reaction Animation Layer */}
            <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
              <AnimatePresence mode="popLayout">
                {reactions.map(r => (
                  <motion.div
                    key={r.id}
                    initial={{ y: '110%', opacity: 0, scale: 0.5, x: `${r.x}%` }}
                    animate={{ 
                      y: '-10%', 
                      opacity: [0, 1, 1, 0], 
                      scale: [0.5, 1.2, 1, 0.8],
                      x: [`${r.x}%`, `${r.x + (Math.random() * 10 - 5)}%`, `${r.x}%`]
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: r.duration, ease: "easeOut" }}
                    className="absolute bottom-0"
                  >
                    <div className="relative text-3xl">
                      {r.type === 'candle' ? '🕯️' : '❤️'}
                      <div className={`absolute inset-0 blur-xl opacity-30 ${r.type === 'candle' ? 'bg-accent-gold' : 'bg-red-500'}`} />
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            
            {/* Reaction Buttons */}
            <div className="absolute right-6 bottom-32 flex flex-col gap-4 z-30">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => addReaction('candle')}
                className="flex items-center justify-center w-14 h-14 bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl transition-all hover:bg-accent-gold hover:border-accent-gold"
              >
                <span className="text-xl">🕯️</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => addReaction('heart')}
                className="flex items-center justify-center w-14 h-14 bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl transition-all hover:bg-red-500 hover:border-red-500"
              >
                <span className="text-xl">❤️</span>
              </motion.button>
            </div>

            {/* Player Info Overlay */}
            <div className={`absolute bottom-0 left-0 right-0 p-6 flex justify-between items-center bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-all duration-500 z-40 ${isHoveringVideo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10">
                  <div className="w-8 h-8 rounded-full bg-accent-gold p-0.5">
                    <img src="https://i.pravatar.cc/150?u=host" className="w-full h-full rounded-full object-cover border-2 border-black" alt="" />
                  </div>
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-widest text-accent-gold">Host</p>
                    <p className="text-white text-[10px] font-bold">{stream.organizer}</p>
                  </div>
                </div>
                
                <div className="hidden md:flex items-center gap-2 bg-white/5 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/10 text-white/40 text-[9px] font-bold uppercase tracking-widest">
                  <SafeIcon icon={FiActivity} className="text-accent-gold" />
                  HD Connection
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button className="p-3 bg-white/5 text-white/60 rounded-xl hover:bg-white/10 hover:text-white transition-all border border-white/10 backdrop-blur-md">
                  <SafeIcon icon={FiMaximize2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Platform Status (Mobile) */}
          <div className="lg:hidden p-4 bg-accent-navy border-t border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon 
                icon={platforms.find(p => p.id === activePlatform)?.icon} 
                className={platforms.find(p => p.id === activePlatform)?.color}
              />
              <span className="text-white/60 text-[10px] font-bold uppercase tracking-widest">
                Streaming via {platforms.find(p => p.id === activePlatform)?.name}
              </span>
            </div>
            <button 
              onClick={() => setShowSourceMenu(true)}
              className="text-accent-gold text-[10px] font-black uppercase tracking-widest"
            >
              Change Platform
            </button>
          </div>
        </div>

        {/* Sidebar - Chat */}
        <div className="w-full lg:w-[420px] border-l border-white/5 flex flex-col bg-accent-navy relative z-20 h-[50vh] lg:h-auto">
          <LiveChat isStreamView />
        </div>
      </div>

      {/* ✅ FIXED: ShareModal now wired to the share button */}
      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        title={stream.title}
        url={shareUrl}
        description={`Join the live memorial stream: ${stream.title} — hosted by ${stream.organizer}`}
      />
    </>
  );
};

export default LiveStreamView;